import React from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaComments } from "react-icons/fa";
import { MdOutlinePayments } from "react-icons/md";
import type { IconBaseProps } from "react-icons";
import avatar from "../../../images/avatar_icon.png";
import { CALENDLY_TALK_TO_US_URL } from "../../../constants";

import changePasswordSvg from "../../../images/changePassword.svg";
import logoutSvg from "../../../images/logout.svg";
import { isGoogleLoginUser } from "../../../app/utils/envUtils";
import LogoutModal from "../../../app/components/LogoutModal/LogoutModal";
import SubscriptionsBillingModal from "../SubscriptionsBillingModal/SubscriptionsBillingModal";

const TalkIcon = FaComments as React.ComponentType<IconBaseProps>;
const PaymentsIcon = MdOutlinePayments as React.ComponentType<IconBaseProps>;

interface ProfileDropdownProps {
  logoutHandler: () => void;
  onOpenSubscriptionsBilling?: () => void;
  className?: string;
  menuClassName?: string;
  avatarClassName?: string;
  drop?: "down" | "start" | "up";
  onToggle?: (isOpen: boolean) => void;
  show?: boolean;
}

const menuItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "8px 16px",
};

const menuStyle = {
  width: "max-content",
  padding: "4px 0px",
};

const avatarIconStyle = {
  width: "34px",
  height: "34px",
};

const changePasswordIconStyle = {
  width: "16px",
  height: "16px",
};

const logoutIconStyle = {
  width: "14px",
  height: "14px",
};

const talkIconStyle = {
  width: "16px",
  height: "16px",
  flexShrink: 0,
};

const paymentsIconStyle = {
  width: "16px",
  height: "16px",
  flexShrink: 0,
};

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  logoutHandler,
  onOpenSubscriptionsBilling,
  className = "mx-auto patch-dropdown",
  menuClassName = "user-dropdown-toggle",
  avatarClassName = "user-img",
  drop = "down",
  onToggle,
  show,
}) => {
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [showSubscriptionsModal, setShowSubscriptionsModal] = React.useState(false);

  const openLogoutModal = () => setShowLogoutModal(true);
  const closeLogoutModal = () => setShowLogoutModal(false);
  const closeSubscriptionsModal = () => setShowSubscriptionsModal(false);

  const openSubscriptionsBilling = () => {
    if (onOpenSubscriptionsBilling) {
      onOpenSubscriptionsBilling();
      return;
    }
    setShowSubscriptionsModal(true);
  };

  const confirmLogout = () => {
    if (logoutHandler) logoutHandler();
    closeLogoutModal();
  };

  let firstname = localStorage.getItem("JMLFirstname");
  if (!firstname) {
    firstname = "User";
  }

  let lastname = localStorage.getItem("JMLLastname");
  if (!lastname) {
    lastname = "";
  }

  let email = localStorage.getItem("JMLEmail");

  return (
    <Dropdown className={className} drop={drop} onToggle={onToggle} show={show}>
      <Dropdown.Toggle>
        <img
          src={avatar}
          className={avatarClassName}
          loading="lazy"
          alt="avatar"
        />
      </Dropdown.Toggle>
      <Dropdown.Menu className={menuClassName} style={menuStyle} align="end">
        <Dropdown.Item
          style={{
            fontSize: "16px",
            fontWeight: "bold",
            padding: "0px",
            cursor: "default",
            pointerEvents: "none",
            userSelect: "none",
            borderBottom: "2px outset gray",
          }}
        >
          <div style={menuItemStyle}>
            <img src={avatar} style={avatarIconStyle} alt="avatarIcon" />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>
                {firstname} {lastname}
              </span>
              {email && (
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "normal",
                    color: "gray",
                  }}
                >
                  {email}
                </span>
              )}
            </div>
          </div>
        </Dropdown.Item>
        <Dropdown.Item
          style={{ whiteSpace: "nowrap", padding: 0 }}
          onClick={openSubscriptionsBilling}
        >
          <div style={menuItemStyle}>
            <PaymentsIcon style={paymentsIconStyle} aria-hidden />
            <span>Subscriptions and Billing</span>
          </div>
        </Dropdown.Item>
        {isGoogleLoginUser() ? (
          <OverlayTrigger
            placement="left"
            overlay={
              <Tooltip id="change-password" style={{ position: "fixed" }}>
                Not available for Google Login Users
              </Tooltip>
            }
          >
            <span>
              <Dropdown.Item
                href="/change-password"
                style={{ whiteSpace: "nowrap", padding: 0 }}
                disabled
              >
                <div style={menuItemStyle}>
                  <img
                    src={changePasswordSvg}
                    style={changePasswordIconStyle}
                    alt="changePassword"
                  />
                  <span>Change password</span>
                </div>
              </Dropdown.Item>
            </span>
          </OverlayTrigger>
        ) : (
          <Dropdown.Item
            href="/change-password"
            style={{ whiteSpace: "nowrap", padding: 0 }}
          >
            <div style={menuItemStyle}>
              <img
                src={changePasswordSvg}
                style={changePasswordIconStyle}
                alt="changePassword"
              />
              <span>Change password</span>
            </div>
          </Dropdown.Item>
        )}
        <Dropdown.Item
          as="a"
          href={CALENDLY_TALK_TO_US_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ whiteSpace: "nowrap", padding: 0 }}
          title="Direct to team. No SDRs."
        >
          <div style={menuItemStyle}>
            <TalkIcon style={talkIconStyle} aria-hidden />
            <span>Talk to us</span>
          </div>
        </Dropdown.Item>
        <Dropdown.Item style={{ padding: 0 }} onClick={openLogoutModal}>
          <div style={menuItemStyle}>
            <img src={logoutSvg} style={logoutIconStyle} alt="logout" />
            <span>Logout</span>
          </div>
        </Dropdown.Item>
      </Dropdown.Menu>
      <LogoutModal
        show={showLogoutModal}
        onHide={closeLogoutModal}
        onConfirm={confirmLogout}
      />
      <SubscriptionsBillingModal
        show={showSubscriptionsModal}
        onHide={closeSubscriptionsModal}
      />
    </Dropdown>
  );
};

export default ProfileDropdown;
